import React from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  Button,
  useTheme,
} from "@mui/material";
import {
  TABLE_MAX_HEIGHT,
  ROW_HOVER_SHADOW,
} from "../constants";
import { brand } from "../config/theme";

export interface ColumnDef {
  id: string;
  label: string;
  sortable?: boolean;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
  align?: "left" | "right" | "center";
  scope?: string;
  /**
   * Optional spanning header. Consecutive columns sharing a `group` render
   * under one merged top-level heading, with their own labels beneath — used by
   * My Players to put a UD and a DK sub-column under a single metric name.
   * Columns without a group span both header rows.
   */
  group?: string;
  /** Accent color for the sub-header label (e.g. the platform color). */
  labelColor?: string;
}

export interface SortState {
  key: string;
  dir: "asc" | "desc";
}

export interface DataTableShellProps<T> {
  columns: ColumnDef[];
  /** Renders a full <TableRow> (with all <TableCell>s) for each item. */
  renderRow: (item: T, index: number) => React.ReactNode;
  items: T[];
  totalCount: number;
  visibleCount: number;
  onShowMore: () => void;
  emptyState?: React.ReactNode;
  sortState?: SortState;
  onSort?: (colId: string) => void;
  tableContainerRef?: React.RefObject<HTMLDivElement | null>;
  tableLabel?: string;
  /** Whether to render an empty leading column (e.g. expand toggle in MyPlayers). */
  hasLeadingColumn?: boolean;
}

export function DataTableShell<T>({
  columns,
  renderRow,
  items,
  totalCount,
  visibleCount,
  onShowMore,
  emptyState,
  sortState,
  onSort,
  tableContainerRef,
  tableLabel,
  hasLeadingColumn,
}: DataTableShellProps<T>) {
  const theme = useTheme();
  const darkMode = theme.palette.mode === "dark";
  const primaryColor = darkMode ? brand.primary.dark : brand.primary.light;
  const zebraBg = darkMode ? "#1A1A1A" : "#FAFAF8";

  const colCount = columns.length + (hasLeadingColumn ? 1 : 0);

  // Coalesce runs of columns sharing a `group` so the top header row can span
  // them. Ungrouped columns stay standalone and simply span both header rows.
  const grouped = columns.some(c => c.group);
  const headerSpans: Array<{ group?: string; columns: ColumnDef[] }> = [];
  for (const col of columns) {
    const last = headerSpans[headerSpans.length - 1];
    if (col.group && last?.group === col.group) last.columns.push(col);
    else headerSpans.push({ group: col.group, columns: [col] });
  }

  const sortLabelFor = (col: ColumnDef) => {
    if (!col.sortable || !onSort) return <>{col.label}</>;
    const active = sortState?.key === col.id;
    return (
      <TableSortLabel
        active={active}
        direction={active ? sortState!.dir : "asc"}
        onClick={() => onSort(col.id)}
      >
        {col.label}
      </TableSortLabel>
    );
  };

  return (
    <Box
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: 2, overflow: "hidden", position: "relative", mx: 3, mb: 3, mt: 1 }}
    >
      <TableContainer
        ref={tableContainerRef}
        sx={{ maxHeight: TABLE_MAX_HEIGHT, overflowY: "auto", overflowX: "hidden" }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label={tableLabel}
          sx={{
            "& .MuiTableBody-root .MuiTableRow-root": {
              transition: "box-shadow 0.12s ease-out",
              "&:hover": {
                boxShadow: ROW_HOVER_SHADOW,
                "& .MuiTableCell-root:first-of-type": {
                  borderLeft: `3px solid ${primaryColor}`,
                },
              },
            },
            "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
              bgcolor: zebraBg,
            },
          }}
        >
          <TableHead>
            {grouped ? (
              <>
                {/* Top tier: merged group headings; ungrouped columns span both rows. */}
                <TableRow>
                  {hasLeadingColumn && (
                    <TableCell scope="col" rowSpan={2} sx={{ width: 40, p: 0 }} />
                  )}
                  {headerSpans.map(span =>
                    span.group ? (
                      <TableCell
                        key={`group-${span.group}`}
                        scope="colgroup"
                        colSpan={span.columns.length}
                        align="center"
                        sx={{ fontWeight: 700, whiteSpace: "nowrap", px: 1, pb: 0.25, borderBottom: "none" }}
                      >
                        {span.group}
                      </TableCell>
                    ) : (
                      <TableCell
                        key={span.columns[0].id}
                        scope={span.columns[0].scope ?? "col"}
                        rowSpan={2}
                        sx={{
                          minWidth: span.columns[0].minWidth,
                          maxWidth: span.columns[0].maxWidth,
                          width: span.columns[0].width,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          px: 1.5,
                          textAlign: span.columns[0].align ?? "left",
                        }}
                        sortDirection={
                          span.columns[0].sortable && sortState?.key === span.columns[0].id ? sortState.dir : false
                        }
                      >
                        {sortLabelFor(span.columns[0])}
                      </TableCell>
                    )
                  )}
                </TableRow>
                {/* Second tier: only the grouped columns' own labels. */}
                <TableRow>
                  {columns.filter(c => c.group).map(col => (
                    <TableCell
                      key={col.id}
                      scope="col"
                      sx={{
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                        width: col.width,
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: "0.04em",
                        color: col.labelColor,
                        whiteSpace: "nowrap",
                        px: 1,
                        pt: 0,
                        textAlign: col.align ?? "left",
                      }}
                      sortDirection={col.sortable && sortState?.key === col.id ? sortState.dir : false}
                    >
                      {sortLabelFor(col)}
                    </TableCell>
                  ))}
                </TableRow>
              </>
            ) : (
              <TableRow>
                {hasLeadingColumn && (
                  <TableCell scope="col" sx={{ width: 40, p: 0 }} />
                )}
                {columns.map(col => (
                  <TableCell
                    key={col.id}
                    scope={col.scope ?? "col"}
                    sx={{
                      minWidth: col.minWidth,
                      maxWidth: col.maxWidth,
                      width: col.width,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      px: 1.5,
                      textAlign: col.align ?? "left",
                    }}
                    sortDirection={
                      col.sortable && sortState?.key === col.id ? sortState.dir : false
                    }
                  >
                    {sortLabelFor(col)}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 6 }}>
                  {emptyState ?? "No results."}
                </TableCell>
              </TableRow>
            ) : (
              items.map((item, idx) => renderRow(item, idx))
            )}
            {visibleCount < totalCount && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={onShowMore}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                  >
                    Show more ({totalCount - visibleCount} remaining)
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
